import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { API_ENDPOINTS } from '../../../../config/api';

const ApplicationSettingsList = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Input type labels for display
  const INPUT_TYPES = {
    text: 'Text',
    textarea: 'Textarea',
    number: 'Number',
    email: 'Email',
    password: 'Password',
    select: 'Select',
    radio: 'Radio',
    checkbox: 'Checkbox',
    date: 'Date',
    file: 'File'
  };

  useEffect(() => {
    fetchApplicationSettings();
  }, []);

  const fetchApplicationSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }

      const endpoint = API_ENDPOINTS.APPLICATION_SETTINGS?.BASE || 
                      `${API_ENDPOINTS.APPLICATION_TYPES.BASE.replace('types', 'settings')}`;
      
      // console.log('Fetching settings from endpoint:', endpoint);
      
      const response = await axios.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // console.log('Settings response:', response.data);
      setSettings(response.data.data || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching application settings:', error);
      setError('Failed to fetch application settings. ' + (error.response?.data?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application setting?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const endpoint = API_ENDPOINTS.APPLICATION_SETTINGS?.DELETE?.(id) || 
                      `${API_ENDPOINTS.APPLICATION_SETTINGS?.BASE || 
                        API_ENDPOINTS.APPLICATION_TYPES.BASE.replace('types', 'settings')}/${id}`;
      
      await axios.delete(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh the list after deletion
      fetchApplicationSettings();
    } catch (error) {
      console.error('Error deleting application setting:', error);
      setError('Failed to delete application setting. ' + (error.response?.data?.message || ''));
    }
  };

  // Helper function to get input type label
  const getInputTypeLabel = (type) => {
    return INPUT_TYPES[type] || type;
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 30) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              {/* Application Settings */}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/application-settings/create')}
            >
              Add New Setting
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : settings.length === 0 ? (
            <Alert severity="info">No application settings found</Alert>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="application settings table">
                <TableHead>
                  <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Field Name</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Input Type</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settings
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((setting, index) => (
                      <TableRow key={setting.id}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{setting.field_name}</TableCell>
                        <TableCell>{setting.slug}</TableCell>
                        <TableCell>
                          <Chip 
                            label={getInputTypeLabel(setting.input_type)} 
                            color={['select', 'radio', 'checkbox'].includes(setting.input_type) ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{setting.application_setting_type?.type || '-'}</TableCell>
                        <TableCell align="right">
                          {/* <IconButton
                            color="info"
                            onClick={() => navigate(`/application-settings/view/${setting.id}`)}
                            title="View"
                          >
                            <VisibilityIcon />
                          </IconButton> */}
                          <IconButton
                            color="primary"
                            onClick={() => navigate(`/application-settings/edit/${setting.id}`)}
                            title="Edit"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleDelete(setting.id)}
                            title="Delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={settings.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ApplicationSettingsList; 